<template>
  <div class="combook-binding-top">
    <input
      ref="fileInput"
      type="file"
      accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif"
      style="display: none"
      @change="handleImageUpload"
    />

    <h1 class="title-page" style="margin-top:0px;">
      <v-icon class="material-icons-outlined">import_contacts</v-icon>
      <br />表紙を作成
    </h1>

    <p v-if="!imageFile" class="text-xs-center">
      写真を選んで追加
      <a
        href=""
        @click.prevent="openCoverGeneratorManualFullScreenDialog = true"
        >操作方法</a
      >
    </p>
    <p v-else class="text-xs-center">
      位置やサイズを編集
      <a
        href=""
        @click.prevent="openCoverGeneratorManualFullScreenDialog = true"
        >操作方法</a
      >
    </p>

    <div class="editor-section">
      <div
        :class="[
          'cover-preview-wrapper',
          { 'cover-preview-pastel': currentDesignType === 'pastel' }
        ]"
        :style="{
          width: previewWidth + 'px',
          height: totalCoverHeight + 'px',
          '--scale-factor': scaleToPreview
        }"
      >
        <component
          :is="currentCoverComponent"
          :organization-name="organizationName"
          :kid-name="kidName"
          :display-year-month="displayYearMonth"
          :start-month="startMonth"
          :end-month="endMonth"
        >
          <template #photo-editor>
            <figure :class="currentPhotoClass">
              <div
                v-if="!imageUrl"
                class="upload-button-wrapper"
                @click="$refs.fileInput.click()"
              >
                <span class="material-icons-outlined upload-icon"
                  >add_photo_alternate</span
                >
                <span class="upload-text">写真を追加</span>
              </div>
              <image-cropper
                v-else
                ref="photoEditor"
                :image-url="imageUrl"
                :min-image-width="currentMinImageWidth"
                :min-image-height="currentMinImageHeight"
                :scale-to-preview="scaleToPreview"
              />
            </figure>
          </template>
        </component>

        <!-- 塗り足しエリアのガイド -->
        <div
          class="bleed-area-guide"
          :style="{
            top: bleedTopPreview + 'px',
            width: safeAreaPreviewWidth + 'px',
            height: safeAreaPreviewHeight + 'px'
          }"
        ></div>
      </div>

      <div class="style-selector">
        <div class="style-toggle-wrapper">
          <v-btn-toggle
            v-model="currentDesignType"
            mandatory
            :class="['style-toggle', { 'style-toggle--disabled': !imageUrl }]"
            :style="{ width: previewWidth + 'px' }"
            :disabled="!imageUrl"
          >
            <v-btn value="pastel" flat :disabled="!imageUrl">
              パステル
            </v-btn>
            <v-btn
              value="simple"
              flat
              :disabled="!imageUrl || !canUseSimpleDesign"
            >
              シンプル
            </v-btn>
          </v-btn-toggle>
        </div>
      </div>

      <div class="replace-button-wrapper">
        <button
          class="replace-button"
          style="display: flex; align-items: center; gap: 8px;"
          :disabled="!imageUrl"
          @click="$refs.fileInput.click()"
        >
          <span class="material-icons">image</span>
          写真の入れ替え
        </button>
      </div>
    </div>

    <span
      v-if="attentionMessage"
      class="validation-error-message attention-text"
    >
      {{ attentionMessage }}
    </span>

    <div class="image-size-notice">
      <p class="notice-title">【写真の配置について】</p>
      <p class="notice-text">
        写真がページの端まできれいに印刷されるように、オレンジの点線より外側まで広げて配置してください。
      </p>
    </div>

    <v-layout justify-center column pb-4 align-center>
      <v-btn
        class="v-btn"
        style="width: 100%; margin-bottom: 16px;"
        color="primary"
        :disabled="!imageUrl || uploading"
        @click="showConfirmDialog = true"
      >
        次へ進む
      </v-btn>

      <v-btn
        class="v-btn"
        style="width: 100%;"
        color="negative"
        @click="$emit('back')"
      >
        戻る
      </v-btn>
    </v-layout>

    <confirm-navigation-dialog
      :dialog="showConfirmDialog"
      message="この表紙に決定しますか？"
      @confirm="handleConfirmNext"
      @cancel="showConfirmDialog = false"
    />

    <loading-modal
      :value="showLoadingModal"
      :icon="'import_contacts'"
      :message="`連絡帳の表紙を作成中です\nこの画面を表示してお待ち下さい`"
    />

    <IosSafariTabBarBackgroundBar
      v-show="openCoverGeneratorManualFullScreenDialog"
    />

    <CoverGeneratorManualFullScreenDialog
      :dialog="openCoverGeneratorManualFullScreenDialog"
      @close-dialog="openCoverGeneratorManualFullScreenDialog = false"
    >
    </CoverGeneratorManualFullScreenDialog>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import SimpleCover from "../Covers/SimpleCover.vue";
import PastelCover from "../Covers/PastelCover.vue";
import ImageCropper from "../ImageCropper.vue";
import ConfirmNavigationDialog from "../../../../components/ConfirmNavigationDialog.vue";
import LoadingModal from "../../../../components/LoadingModal.vue";
import IosSafariTabBarBackgroundBar from "packs/site/components/IosSafariTabBarBackgroundBar.vue";
import CoverGeneratorManualFullScreenDialog from "../Dialogs/CoverGeneratorManualFullScreenDialog.vue";
import CombookBindingCover from "../../../../models/CombookBindingCover";
import ApiErrors from "../../../../lib/lookmee_photo/ApiErrors";
import ApiErrorHandler from "../../../../mixins/ApiErrorHandler";
import {
  SIMPLE_MIN_IMAGE_SIZE,
  PASTEL_MIN_IMAGE_SIZE,
  COVER_COMMON_SIZES
} from "../CoverConstants";

export default Vue.extend({
  name: "EditCoverImage",

  components: {
    SimpleCover,
    PastelCover,
    ImageCropper,
    ConfirmNavigationDialog,
    LoadingModal,
    IosSafariTabBarBackgroundBar,
    CoverGeneratorManualFullScreenDialog
  },

  mixins: [ApiErrorHandler],

  props: {
    organizationId: {
      type: Number,
      required: true
    },
    organizationName: {
      type: String,
      default: ""
    },
    kidName: {
      type: String,
      default: ""
    },
    selectedDesignType: {
      type: String,
      default: null
    },
    selectedKid: {
      type: Object,
      default: null
    },
    selectedPeriod: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      previewWidth: COVER_COMMON_SIZES.previewWidth,
      imageUrl: "" as string,
      imageFile: null as File | null,
      originalImageWidth: null as number | null,
      originalImageHeight: null as number | null,
      attentionMessage: "" as string,
      uploading: false,
      showConfirmDialog: false,
      showLoadingModal: false,
      coverId: null as number | null,
      openCoverGeneratorManualFullScreenDialog: false
    };
  },

  computed: {
    currentDesignType: {
      get(this: any): "simple" | "pastel" {
        return this.selectedDesignType === "simple" ? "simple" : "pastel";
      },
      set(this: any, value: "simple" | "pastel") {
        this.$emit("update:selectedDesignType", value);
      }
    },

    currentCoverComponent(this: any): string {
      return this.currentDesignType === "simple"
        ? "simple-cover"
        : "pastel-cover";
    },

    currentPhotoClass(this: any): string {
      return this.currentDesignType === "simple"
        ? "cover-front-photo-simple"
        : "cover-front-photo-pastel";
    },

    currentMinImageWidth(this: any): number {
      return this.currentDesignType === "simple"
        ? SIMPLE_MIN_IMAGE_SIZE.width
        : PASTEL_MIN_IMAGE_SIZE.width;
    },

    currentMinImageHeight(this: any): number {
      return this.currentDesignType === "simple"
        ? SIMPLE_MIN_IMAGE_SIZE.height
        : PASTEL_MIN_IMAGE_SIZE.height;
    },

    // 現在読み込まれている画像で「シンプル」デザインが利用可能かどうか
    canUseSimpleDesign(this: any): boolean {
      if (!this.originalImageWidth || !this.originalImageHeight) {
        // 画像未選択時やサイズ未取得時は、とりあえず有効扱い（ボタン自体は !imageUrl で無効化される）
        return true;
      }
      return (
        this.originalImageWidth >= SIMPLE_MIN_IMAGE_SIZE.width &&
        this.originalImageHeight >= SIMPLE_MIN_IMAGE_SIZE.height
      );
    },

    // プレビュー表示用の拡大率（最大0.158）
    scaleToPreview(this: any): number {
      return this.previewWidth / COVER_COMMON_SIZES.coverWidth;
    },

    safeAreaPreviewWidth(this: any): number {
      return Math.round(
        COVER_COMMON_SIZES.safeCoverWidth * this.scaleToPreview
      );
    },

    safeAreaPreviewHeight(this: any): number {
      return Math.round(
        COVER_COMMON_SIZES.safeCoverHeight * this.scaleToPreview
      );
    },

    bleedTopPreview(this: any): number {
      return Math.round(COVER_COMMON_SIZES.bleedMargin * this.scaleToPreview);
    },

    totalCoverHeight(this: any): number {
      return Math.round(COVER_COMMON_SIZES.coverHeight * this.scaleToPreview);
    },

    startMonth() {
      return this.selectedPeriod ? this.selectedPeriod.displayStartMonth : "";
    },
    endMonth() {
      return this.selectedPeriod ? this.selectedPeriod.displayEndMonth : "";
    },

    displayYearMonth() {
      return this.selectedPeriod ? this.selectedPeriod.displayYearMonth : "";
    }
  },

  watch: {
    // スタイルが変更されたときにcropperをリセット
    // これにより画像の位置が再調整されるが、画像データは保持される
    currentDesignType() {
      if (this.imageUrl) {
        this.$nextTick(() => {
          const photoEditor = this.$refs.photoEditor as any;
          if (photoEditor && photoEditor.reset) {
            photoEditor.reset();
          }
        });
      }
    }
  },

  mounted() {
    this.updatePreviewWidth();
    window.addEventListener("resize", this.updatePreviewWidth);
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.updatePreviewWidth);
  },

  methods: {
    updatePreviewWidth() {
      const container = this.$el as HTMLElement;
      if (!container) return;

      // コンテナの幅を取得し、左右に余白(各20px)を確保
      const containerWidth = container.clientWidth;
      const padding = 40; // 左右合わせて40px

      // プレビュー幅を計算(余白を引いて、最大幅を超えないようにする)
      this.previewWidth = Math.min(
        containerWidth - padding,
        COVER_COMMON_SIZES.previewWidth
      );
    },

    isImageSizeValidForCurrentDesign(width: number, height: number): boolean {
      return (
        width >= this.currentMinImageWidth &&
        height >= this.currentMinImageHeight
      );
    },

    handleImageUpload(event: Event) {
      const target = event.target as HTMLInputElement;
      const file = target.files && target.files[0];

      // 以前のエラーメッセージはクリア
      this.attentionMessage = "";

      if (file) {
        // ファイルサイズチェック（20MB = 20 * 1024 * 1024 bytes）
        const maxFileSize = 20 * 1024 * 1024;
        if (file.size > maxFileSize) {
          this.attentionMessage =
            `ファイルサイズが大きすぎます。\n` +
            `ファイルサイズ: ${(file.size / 1024 / 1024).toFixed(2)}MB\n\n` +
            `20MB以下の画像を選択してください。`;
          // inputをリセット
          target.value = "";
          return;
        }

        const reader = new FileReader();
        reader.onload = e => {
          const imageUrl = (e.target && e.target.result) as string;

          // 画像の元のサイズを取得して最小サイズをチェック
          const img = new Image();
          img.onload = () => {
            const width = img.naturalWidth;
            const height = img.naturalHeight;

            // 画像の幅または高さが最小サイズを下回る場合はエラー
            if (!this.isImageSizeValidForCurrentDesign(width, height)) {
              this.attentionMessage =
                `この画像は表紙として使用できません。\n` +
                `画像サイズ: ${width}×${height}ピクセル\n\n` +
                `表紙として使用するには、横幅${this.currentMinImageWidth}ピクセル以上、縦幅${this.currentMinImageHeight}ピクセル以上の画像が必要です。`;
              // inputをリセット
              target.value = "";
              return;
            }

            this.originalImageWidth = width;
            this.originalImageHeight = height;
            this.imageFile = file;
            this.imageUrl = imageUrl;

            // 画像サイズはOKだが、シンプルが利用できないときの注意文言
            if (!this.canUseSimpleDesign) {
              this.attentionMessage =
                `「シンプル」デザインを選択する場合は\n` +
                `横${SIMPLE_MIN_IMAGE_SIZE.width}ピクセル、縦${SIMPLE_MIN_IMAGE_SIZE.height}ピクセル以上の画像をご利用ください。\n\n` +
                `現在の画像サイズ: ${width}×${height}ピクセル`;
            } else {
              // 条件を満たしていれば、ここで既存の注意文言はクリア
              this.attentionMessage = "";
            }
          };
          img.src = imageUrl;
        };
        reader.readAsDataURL(file);
      }
    },

    handleNext() {
      this.$emit("next");
    },

    async handleConfirmNext(this: any) {
      this.showConfirmDialog = false;
      this.showLoadingModal = true;
      this.uploading = true;

      try {
        // 1. 画像をクロップと、表紙レコード作成・S3アップロードURL取得を並列実行
        const [croppedBlob, { uploadUrl, coverId }] = await Promise.all([
          this.cropImage(),
          this.createCombookBindingCover(this.imageFile!.type)
        ]);
        // coverIdを保存
        this.coverId = coverId;
        // 2. クロップ済み画像をS3にアップロード
        await this.uploadCroppedImageToS3(uploadUrl, croppedBlob);
        // 3. S3に画像をアップロード完了したことをサーバーに通知して表紙データを作成リクエスト
        const coverData = await this.generateCombookBindingCover();
        this.$emit("coverGenerated", {
          id: coverData.id,
          thumbnailUrl: coverData.thumbnailUrl
        });
        // 4. 表紙データが作成されるまでポーリングする。(lambdaの実行時間がそこまでかからなければ同期的に実行予定)
        // TODO: ポーリング実装。coverIdを使ってステータスを確認
        this.handleNext();
      } catch (error) {
        this.attentionMessage =
          "画像アップロード中にエラーが発生しました。お手数ですが、再度お試しください。";
        if (error instanceof ApiErrors) {
          const sentry = (this as any).sentry;
          if (sentry && sentry.captureMessage) {
            sentry.captureMessage(error.getErrorMessage());
          }
        }
        console.error(error);
      } finally {
        this.showLoadingModal = false;
        this.uploading = false;
      }
    },

    async cropImage(): Promise<Blob> {
      const photoEditor = this.$refs.photoEditor as any;
      if (!photoEditor) {
        throw new Error("PhotoEditorが初期化されていません。");
      }
      return await photoEditor.getCroppedBlob(this.imageFile!.type);
    },

    async createCombookBindingCover(
      mimeType: string
    ): Promise<{ uploadUrl: string; coverId: number }> {
      const combookBindingCover = new CombookBindingCover({
        organizationId: this.organizationId,
        kidId: this.selectedKid.id,
        year: this.selectedPeriod.calendarYear,
        startMonth: this.selectedPeriod.startMonth,
        endMonth: this.selectedPeriod.endMonth,
        organizationName: this.organizationName,
        kidName: this.kidName,
        mimeType,
        designType: this.currentDesignType
      });
      return await this.$store.dispatch(
        "createCombookBindingCover",
        combookBindingCover
      );
    },

    async uploadCroppedImageToS3(
      uploadUrl: string,
      croppedBlob: Blob
    ): Promise<void> {
      const response = await fetch(uploadUrl, {
        method: "PUT",
        body: croppedBlob,
        headers: {
          "Content-Type": croppedBlob.type
        }
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status}`);
      }
    },

    async generateCombookBindingCover(
      this: any
    ): Promise<{ id: number; thumbnailUrl: string }> {
      return await this.$store.dispatch("generateCombookBindingCover", {
        organizationId: this.organizationId,
        coverId: this.coverId
      });
    }
  }
});
</script>

<style scoped>
.combook-binding-top {
  padding: 0 20px;
  max-width: 350px;
  margin: 0 auto;
}

h2 {
  margin-bottom: 20px;
  font-size: 24px;
  color: #333;
}

.style-selector {
  display: flex;
  justify-content: center;
  gap: 24px;
  margin-bottom: 24px;
}

.style-toggle {
  border: 1px solid #4cb5b8;
  border-radius: 6px;
  padding: 4px;
  gap: 4px;
  box-shadow: unset;
  transition: none;
}

.style-toggle--disabled {
  opacity: 0.4;
  cursor: default;
  pointer-events: none;
}

.style-toggle >>> .v-btn {
  border-radius: 4px;
  font-size: 14px;
  font-weight: 700;
  padding: 4px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  gap: 4px;
  background-color: #ffffff;
  color: #4cb5b8;
}

.style-toggle >>> .v-btn {
  opacity: 1;
}

.style-toggle >>> .v-btn.v-btn--active {
  background-color: #4cb5b8;
  color: #ffffff;
}

.style-toggle >>> .v-btn.v-btn--disabled {
  opacity: 0.4;
  cursor: default;
  pointer-events: none;
}

.style-toggle >>> .v-btn.v-btn--active.v-btn--disabled {
  color: #ffffff !important;
}

.style-toggle >>> .v-btn.v-btn--disabled {
  color: #4cb5b8 !important;
}

/* 画像サイズの都合でシンプルが選べないときの見た目だけの無効化状態 */
.style-toggle__btn--unavailable {
  opacity: 0.4;
  cursor: default;
}

.editor-section {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.replace-button-wrapper {
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
}

.replace-button {
  font-weight: 700;
  padding: 8px 16px;
  background-color: #ffffff;
  color: #4cb5b8;
  border: 1px solid #4cb5b8;
  border-radius: 36px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  gap: 4px;
}

.replace-icon {
  font-size: 18px;
}

.replace-button:hover {
  background-color: #f5f5f5;
}

.replace-button:active {
  transform: scale(0.98);
}

.replace-button:disabled {
  opacity: 0.4;
  cursor: default;
  pointer-events: none;
}

.input-section {
  width: 100%;
  margin-bottom: 20px;
}

.image-size-notice {
  margin-top: 20px;
  text-align: left;
  max-width: 350px;
  margin-bottom: 24px;
}

.notice-title {
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: 400;
}

.notice-text {
  font-size: 12px;
  line-height: 1.6;
  color: #333;
  margin: 0;
}

/* 表紙プレビューの共通スタイル */
.cover-preview-wrapper {
  position: relative;
  box-shadow: 6.39px 12.77px 12.77px 0px #00000014;
  margin-bottom: 30px;
  border: 1px solid #eaeae8;
  font-weight: 600;
  color: #47372c;
  background-color: #ffffff;
}

.cover-preview-pastel {
  background-image: url("../../../../assets/images/combook_binding/cover_front_bg_pastel.png");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.bleed-area-guide {
  position: absolute;
  left: 0;
  border-top: 2px dashed rgba(255, 165, 0, 0.6);
  border-right: 2px dashed rgba(255, 165, 0, 0.6);
  border-bottom: 2px dashed rgba(255, 165, 0, 0.6);
  pointer-events: none;
}

/* 写真を追加UIのスタイル */
.upload-button-wrapper {
  width: 100%;
  height: 100%;
  background-color: #f3f2f1;
  border: 2px dashed #4cb5b8;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s;
}

.upload-button-wrapper:hover {
  background-color: #e8e6e5;
}

.upload-icon {
  font-size: 48px;
  color: #4cb5b8;
  margin-bottom: 12px;
}

.upload-text {
  font-size: 14px;
  color: #4cb5b8;
  font-weight: 500;
}

.cover-front-photo-simple {
  width: calc(1783px * var(--scale-factor));
  height: calc(1733px * var(--scale-factor));
  position: absolute;
  bottom: 0;
  left: 0;
  overflow: hidden;
  margin: 0;
  padding: 0;
}

.cover-front-photo-pastel {
  width: calc(1514px * var(--scale-factor));
  height: calc(1532px * var(--scale-factor));
  position: absolute;
  top: calc(829px * var(--scale-factor));
  right: calc(142px * var(--scale-factor));
  margin: 0;
  padding: 0;
  overflow: hidden;
  clip-path: url(#pastelClipPath);
}

.attention-text {
  font-size: 12px;
  line-height: 150%;
  color: #fa6053;
  white-space: pre-line;
}
</style>
